import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { logout } from "../../store/slices/authSlice";
import { useGetUserMembershipStatusQuery, useGetProfileQuery } from "../../store/slices/apiSlice";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../config/api";
import {
    ChevronDown,
    Shield,
    Smartphone,
    Users,
    Globe,
    TrendingUp,
    User,
    Wallet,
    LogOut,
    Store,
    Home,
    Heart
} from "lucide-react";
import logo from "../../assets/DWBRANDprview.png";
import UserAvatar from "./UserAvatar";

const navLinks = [
    { name: "Home", path: "/" },
    { name: "Solar", path: "/solar" },
    { name: "About", path: "/about" },
    { name: "Projects", path: "/projects" },
    { name: "Marketplace", path: "/marketplace" },
    { name: "Investment", path: "/investment" },
    { name: "Blogs", path: "/blogs" },
    { name: "Careers", path: "/careers" },
    { name: "Contact", path: "/contact" },
];  

const nigerianDiasporaLinks = [
    { name: "Overview", path: "/nigerian-diaspora-investors", icon: Users },
    { name: "United Kingdom", path: "/nigerian-diaspora-investors/uk", icon: Globe },
    { name: "United States", path: "/nigerian-diaspora-investors/us", icon: Globe },
    { name: "Europe", path: "/nigerian-diaspora-investors/europe", icon: Globe },
    { name: "Asia", path: "/nigerian-diaspora-investors/asia", icon: Globe },
];

const investmentLinks = [
    { name: "Overview", path: "/investment", icon: TrendingUp },
    { name: "Real Estate Investment Nigeria", path: "/investment/real-estate-investment-nigeria", icon: TrendingUp },
    { name: "Real Estate Investing From Abroad", path: "/investment/real-estate-investing-from-abroad", icon: Globe },
    { name: "Commercial Property Investment Nigeria", path: "/investment/commercial-property-investment-nigeria", icon: TrendingUp },
    { name: "Residential Property Investment Nigeria", path: "/investment/residential-property-investment-nigeria", icon: TrendingUp },
    { name: "Fractional Property Investment Nigeria", path: "/investment/fractional-property-investment-nigeria", icon: TrendingUp },
];

const Navbar = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [investmentDropdownOpen, setInvestmentDropdownOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    // Mobile dropdown states
    const [mobileDropdownOpen, setMobileDropdownOpen] = useState<string | null>(null);

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((state: RootState) => state.auth);
    const isActive = (path: string) => location.pathname === path;
    
    // Check if we're on a project detail page
    const isProjectDetailPage = location.pathname.startsWith('/projects/') && location.pathname !== '/projects';
    
    // Check if user is regular user/dealer or admin
    const isRegularUser = user?.role === 'USER' || user?.role === 'DEALER';
    const isAdmin = user?.role === 'SU' || user?.role === 'AU';
    const isDealer = user?.role === 'DEALER';

    // Filter navLinks to hide Marketplace for dealers
    const filteredNavLinks = isDealer 
        ? navLinks.filter(link => link.name !== 'Marketplace')
        : navLinks;

    // Check membership status for regular users
    const { data: membershipStatusResponse, isLoading: isLoadingMembership } = useGetUserMembershipStatusQuery(undefined, {
        skip: !isRegularUser,
    });
    const hasActiveMembership = membershipStatusResponse?.data?.is_active || false;

    // Fetch profile data to get profile image
    const { data: profileData } = useGetProfileQuery(
        { role: user?.role || 'USER' },
        { skip: !user }
    );
    const profileImage = profileData?.data?.profile_image;
    
    // Construct profile image URL
    const getProfileImageUrl = (): string | null => {
        if (!profileImage) return null;
        const APP_URL = API_BASE_URL;
        if (profileImage.startsWith('http')) {
            return profileImage;
        }
        return `${APP_URL}/uploads/${profileImage}`;
    };
    const profileImageUrl = getProfileImageUrl();
    const [imageError, setImageError] = React.useState(false);

    // Reset image error when profile image changes
    React.useEffect(() => {
        setImageError(false);
    }, [profileImage]);

    const handleLogout = () => {
        dispatch(logout());
        toast.success('Logged out successfully!');
        navigate('/');
        setUserMenuOpen(false);
    };

    const getUserProfilePath = () => {
        if (user?.role === 'DEALER') {
            return '/dealer/profile';
        } else if (user?.role === 'USER') {
            return '/user/profile';
        }
        return '/profile';
    };

    const getUserWalletPath = () => {
        if (user?.role === 'DEALER') {
            return '/dealer/wallet';
        } else if (user?.role === 'USER') {
            return '/user/wallet';
        }
        return '/wallet';
    };

    const getUserMarketplacePath = () => {
        return '/marketplace';
    };

    // Mobile dropdown helpers
    const toggleMobileDropdown = (dropdownType: string) => {
        setMobileDropdownOpen(mobileDropdownOpen === dropdownType ? null : dropdownType);
    };

    const closeMobileMenu = () => {
        setMenuOpen(false);
        setMobileDropdownOpen(null);
    };

    const isNigerianDiasporaActive = location.pathname.startsWith('/nigerian-diaspora-investors');
    const isInvestmentActive = location.pathname.startsWith('/investment');

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 20;
            setScrolled(isScrolled);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu when route changes
    useEffect(() => {
        setMenuOpen(false);
        setMobileDropdownOpen(null);
        setUserMenuOpen(false);
    }, [location.pathname]);

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (userMenuOpen && !target.closest('.user-menu-container')) {
                setUserMenuOpen(false);
            }
        };

        if (userMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [userMenuOpen]);

    return (
        <>
            {/* Mobile Menu Overlay */}
            {menuOpen && (
                <div
                    className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
                    onClick={closeMobileMenu}
                    aria-hidden="true"
                />
            )}

            <nav
                className={`sticky top-0 z-50 w-full transition-all duration-300 ease-out backdrop-blur-sm
        ${scrolled
                        ? 'bg-white shadow-md border-b border-gray-100'
                        : 'bg-white/95 shadow-sm'
                    }`}
                aria-label="Main Navigation"
            >
                <div className="container mx-auto flex items-center justify-between px-3 py-2 sm:py-2.5 lg:py-2">
                    {/* Logo & Brand */}
                    <Link
                        to="/"
                        className="flex items-center gap-2 lg:gap-3 group cursor-pointer min-w-fit"
                        aria-label="Home"
                    >
                        <div className="w-10 h-8 sm:w-12 sm:h-9 lg:w-14 lg:h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-sm flex items-center justify-center group-hover:shadow-md transition-all duration-200 group-hover:scale-105">
                            <img
                                src={logo}
                                alt="Diasporawise Logo"
                                className="h-5 w-6 sm:h-6 sm:w-7 lg:h-6 lg:w-8 group-hover:scale-110 transition-transform duration-200"
                            />
                        </div>

                        <div className="min-w-0 hidden sm:block">
                            <h3 className="text-sm lg:text-base font-bold tracking-wide text-gray-900 group-hover:text-gray-700 transition-colors duration-200"
                                style={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                                <span className="text-gray-900">DIASPORA</span>
                                <span className="text-blue-600 font-bold group-hover:text-blue-700 transition-colors duration-200">WISE</span>
                            </h3>
                            <p className="text-xs text-gray-500">Investment Platform</p>
                        </div>
                    </Link>

                    {/* Mobile Center Brand (Small Screens) */}
                    <div className="sm:hidden flex-1 flex justify-center px-2">
                        <div className="text-center">
                            <h3 className="text-sm font-bold tracking-wide text-gray-900"
                                style={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                                <span className="text-gray-900">DIASPORA</span>
                                <span className="text-blue-600 font-bold">WISE</span>
                            </h3>
                            <p className="text-xs text-gray-500">Investment Platform</p>
                        </div>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-2">
                        {filteredNavLinks.map((link, index) => (
                            <React.Fragment key={link.path}>
                                {link.name === "Investment" ? (
                                    <div className="relative group">
                                        <button
                                            className={`relative text-sm font-medium px-3 py-2 transition-all duration-200 rounded-lg flex items-center gap-1
                        ${isInvestmentActive
                                                    ? "text-gray-900 font-semibold bg-gray-50"
                                                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                                                }
                        hover:scale-105 transform`}
                                            style={{
                                                fontFamily: 'Montserrat, Arial, sans-serif',
                                                transitionDelay: `${index * 50}ms`
                                            }}
                                            onMouseEnter={() => setInvestmentDropdownOpen(true)}
                                            onMouseLeave={() => setInvestmentDropdownOpen(false)}
                                        >
                                            <TrendingUp className="w-4 h-4" />
                                            Investment
                                            <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${investmentDropdownOpen ? 'rotate-180' : ''}`} />
                                            {/* Active state indicator */}
                                            {isInvestmentActive && (
                                                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-400 transform scale-x-100 transition-transform duration-200"></div>
                                            )}
                                        </button>

                                        {/* Investment Dropdown Menu */}
                                        <div
                                            className={`absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-200 z-50
                                        ${investmentDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}
                                            onMouseEnter={() => setInvestmentDropdownOpen(true)}
                                            onMouseLeave={() => setInvestmentDropdownOpen(false)}
                                        >
                                            {investmentLinks.map((investmentLink, investmentIndex) => (
                                                <Link
                                                    key={investmentLink.path}
                                                    to={investmentLink.path}
                                                    className={`flex items-center gap-3 px-4 py-3 transition-all duration-200 hover:bg-gray-50 hover:text-gray-900
                                                ${isActive(investmentLink.path) ? 'bg-gray-50 text-gray-900 border-r-4 border-gray-300' : 'text-gray-600'}`}
                                                    style={{
                                                        transitionDelay: investmentDropdownOpen ? `${investmentIndex * 50}ms` : '0ms'
                                                    }}
                                                >
                                                    <investmentLink.icon className="w-4 h-4" />
                                                    <span className="font-medium">{investmentLink.name}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {link.name === "Solar" ? (
                                            <a
                                                href={link.path}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`relative text-sm font-medium px-3 py-2 transition-all duration-200 rounded-lg flex items-center gap-1
                        ${isActive(link.path)
                                                    ? "text-gray-900 font-semibold bg-gray-50"
                                                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                                                }
                        hover:scale-105 transform`}
                                                style={{
                                                    fontFamily: 'Montserrat, Arial, sans-serif',
                                                    transitionDelay: `${index * 50}ms`
                                                }}
                                            >
                                                {link.name}
                                                {/* Active state indicator */}
                                                {isActive(link.path) && (
                                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-400 transform scale-x-100 transition-transform duration-200"></div>
                                                )}
                                            </a>
                                        ) : (
                                            <Link
                                                to={link.path}
                                                className={`relative text-sm font-medium px-3 py-2 transition-all duration-200 rounded-lg flex items-center gap-1
                        ${isActive(link.path)
                                                    ? "text-gray-900 font-semibold bg-gray-50"
                                                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                                                }
                        hover:scale-105 transform`}
                                                aria-current={isActive(link.path) ? "page" : undefined}
                                                style={{
                                                    fontFamily: 'Montserrat, Arial, sans-serif',
                                                    transitionDelay: `${index * 50}ms`
                                                }}
                                            >
                                                {link.name === "Marketplace" && <Store className="w-4 h-4" />}
                                                {link.name}
                                                {/* Active state indicator */}
                                                {isActive(link.path) && (
                                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-400 transform scale-x-100 transition-transform duration-200"></div>
                                                )}
                                            </Link>
                                        )}
                                        {/* Add Stay/Book link after Marketplace for logged-in users (not for dealers) */}
                                        {link.name === "Marketplace" && isRegularUser && !isDealer && (
                                            <Link
                                                to="/apartments"
                                                className={`relative text-sm font-medium px-3 py-2 transition-all duration-200 rounded-lg flex items-center gap-1
                        ${isActive('/apartments') || isActive('/apartments/') || location.pathname.startsWith('/apartments/')
                                                            ? "text-gray-900 font-semibold bg-gray-50"
                                                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                                                        }
                        hover:scale-105 transform`}
                                                style={{
                                                    fontFamily: 'Montserrat, Arial, sans-serif',
                                                    transitionDelay: `${(index + 1) * 50}ms`
                                                }}
                                            >
                                                <Home className="w-4 h-4" />
                                                Stay
                                                {isActive('/apartments') && (
                                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-400 transform scale-x-100 transition-transform duration-200"></div>
                                                )}
                                            </Link>
                                        )}
                                    </>
                                )}

                                {/* Add Nigerian Diaspora dropdown right after Projects */}
                                {link.name === "Projects" && (
                                    <div className="relative group">
                                        <button
                                            className={`relative text-sm font-medium px-3 py-2 transition-all duration-200 rounded-lg flex items-center gap-1
                        ${isNigerianDiasporaActive
                                                    ? "text-gray-900 font-semibold bg-gray-50"
                                                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                                                }
                        hover:scale-105 transform`}
                                            style={{
                                                fontFamily: 'Montserrat, Arial, sans-serif',
                                                transitionDelay: `${(index + 1) * 50}ms`
                                            }}
                                            onMouseEnter={() => setDropdownOpen(true)}
                                            onMouseLeave={() => setDropdownOpen(false)}
                                        >
                                            <Users className="w-4 h-4" />
                                            Nigerian Diaspora
                                            <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} />
                                            {/* Active state indicator */}
                                            {isNigerianDiasporaActive && (
                                                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-400 transform scale-x-100 transition-transform duration-200"></div>
                                            )}
                                        </button>

                                        {/* Dropdown Menu */}
                                        <div
                                            className={`absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-200 z-50
                                        ${dropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}
                                            onMouseEnter={() => setDropdownOpen(true)}
                                            onMouseLeave={() => setDropdownOpen(false)}
                                        >
                                            {nigerianDiasporaLinks.map((diasporaLink, diasporaIndex) => (
                                                <Link
                                                    key={diasporaLink.path}
                                                    to={diasporaLink.path}
                                                    className={`flex items-center gap-3 px-4 py-3 transition-all duration-200 hover:bg-gray-50 hover:text-gray-900
                                                ${isActive(diasporaLink.path) ? 'bg-gray-50 text-gray-900 border-r-4 border-gray-300' : 'text-gray-600'}`}
                                                    style={{
                                                        transitionDelay: dropdownOpen ? `${diasporaIndex * 50}ms` : '0ms'
                                                    }}
                                                >
                                                    <diasporaLink.icon className="w-4 h-4" />
                                                    <span className="font-medium">{diasporaLink.name}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Right Side Actions */}
                    <div className="hidden lg:flex items-center gap-2">
                        {/* Login Section */}
                        {user ? (
                            isRegularUser ? (
                                <div className="relative user-menu-container">
                                    <button
                                        onClick={() => setUserMenuOpen(!userMenuOpen)}
                                        className="relative text-sm font-medium px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 overflow-hidden flex items-center gap-2 group"
                                        style={{
                                            fontFamily: 'Montserrat, Arial, sans-serif'
                                        }}
                                    >
                                        <UserAvatar 
                                            src={profileImageUrl} 
                                            name={user.name} 
                                            size="sm" 
                                        />
                                        <span className="font-semibold text-gray-900">{user.name || user.email?.split('@')[0] || 'User'}</span>
                                        <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {userMenuOpen && (
                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50 user-menu-container">
                                            {user?.role === 'DEALER' ? (
                                                <>
                                                    <Link
                                                        to="/dealer/dashboard"
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                    >
                                                        <Home className="w-4 h-4" />
                                                        <span>Dashboard</span>
                                                    </Link>
                                                    <Link
                                                        to={getUserProfilePath()}
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                    >
                                                        <User className="w-4 h-4" />
                                                        <span>Profile</span>
                                                    </Link>
                                                    <div className="border-t border-gray-200"></div>
                                                    <button
                                                        onClick={handleLogout}
                                                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                    >
                                                        <LogOut className="w-4 h-4" />
                                                        <span>Sign Out</span>
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <Link
                                                        to={getUserProfilePath()}
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                    >
                                                        <User className="w-4 h-4" />
                                                        <span>Profile</span>
                                                    </Link>
                                                    <Link
                                                        to={getUserWalletPath()}
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                    >
                                                        <Wallet className="w-4 h-4" />
                                                        <span>Wallet</span>
                                                    </Link>
                                                    <Link
                                                        to={getUserMarketplacePath()}
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                    >
                                                        <Store className="w-4 h-4" />
                                                        <span>Marketplace</span>
                                                    </Link>
                                                    <Link
                                                        to="/apartments"
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                    >
                                                        <Home className="w-4 h-4" />
                                                        <span>Stay</span>
                                                    </Link>
                                                    <Link
                                                        to="/sponsor/children"
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                    >
                                                        <Heart className="w-4 h-4" />
                                                        <span>Sponsor a Child</span>
                                                    </Link>
                                                    <div className="border-t border-gray-200"></div>
                                                    <button
                                                        onClick={handleLogout}
                                                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                    >
                                                        <LogOut className="w-4 h-4" />
                                                        <span>Sign Out</span>
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Link
                                    to="/admin"
                                    className="relative text-sm font-medium px-4 py-2 bg-gray-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 overflow-hidden flex items-center gap-1 group"
                                    style={{
                                        fontFamily: 'Montserrat, Arial, sans-serif'
                                    }}
                                >
                                    <span className="relative flex items-center gap-1 z-10">
                                        <Shield className="w-4 h-4 transition-transform duration-200 group-hover:rotate-12" />
                                        <span className="font-semibold">Dashboard</span>
                                    </span>
                                    <div className="absolute inset-0 bg-gray-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg"></div>
                                </Link>
                            )
                        ) : (
                            null
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="lg:hidden focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 relative group cursor-pointer p-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-all duration-300 touch-manipulation"
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                        aria-expanded={menuOpen}
                        aria-controls="mobile-menu"
                        onClick={() => setMenuOpen(!menuOpen)}
                    >
                        <div className="w-6 h-6 flex flex-col justify-center items-center relative">
                            <span className={`block w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ease-in-out transform origin-center
              ${menuOpen ? 'rotate-45 translate-y-0' : 'rotate-0 -translate-y-1.5'}`}></span>
                            <span className={`block w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ease-in-out
              ${menuOpen ? 'opacity-0 scale-x-0' : 'opacity-100 scale-x-100'}`}></span>
                            <span className={`block w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ease-in-out transform origin-center
              ${menuOpen ? '-rotate-45 translate-y-0' : 'rotate-0 translate-y-1.5'}`}></span>
                        </div>
                        {/* Ripple effect */}
                        <div className={`absolute inset-0 rounded-xl bg-blue-500 opacity-0 transition-opacity duration-300 ${menuOpen ? 'opacity-10' : 'group-active:opacity-10'}`}></div>
                    </button>
                </div>

                {/* Mobile Menu */}
                <div
                    id="mobile-menu"
                    className={`lg:hidden bg-white/98 backdrop-blur-lg px-4 pb-6 overflow-hidden transition-all duration-500 ease-out border-t border-gray-200/50 shadow-lg relative z-50
          ${menuOpen ? "max-h-screen opacity-100 translate-y-0" : "max-h-0 opacity-0 -translate-y-2 pointer-events-none"}
        `}
                    aria-hidden={!menuOpen}
                >
                    <div className="py-6 space-y-1">
                        {/* Main Navigation Links */}
                        {filteredNavLinks.map((link, idx) => (
                            <React.Fragment key={link.path}>
                                {link.name === "Investment" ? (
                                    <div className="space-y-1">
                                        {/* Investment Dropdown Button */}
                                        <button
                                            onClick={() => toggleMobileDropdown('investment')}
                                            className={`w-full flex items-center justify-between py-4 px-4 font-semibold transition-all duration-300 relative rounded-xl touch-manipulation
                                            ${isInvestmentActive
                                                    ? "text-white bg-gradient-to-r from-blue-600 to-blue-500 shadow-lg"
                                                    : "text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-blue-500 hover:shadow-lg"
                                                }
                                            hover:scale-[1.02] active:scale-[0.98] transform`}
                                            style={{
                                                transitionDelay: menuOpen ? `${idx * 50}ms` : '0ms',
                                                fontFamily: 'Montserrat, Arial, sans-serif'
                                            }}
                                        >
                                            <div className="flex items-center gap-3">
                                                <TrendingUp className="w-5 h-5" />
                                                <span>Investment</span>
                                            </div>
                                            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${mobileDropdownOpen === 'investment' ? 'rotate-180' : ''}`} />
                                            {isInvestmentActive && (
                                                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-white rounded-full"></div>
                                            )}
                                        </button>

                                        {/* Investment Dropdown Items */}
                                        <div className={`overflow-hidden transition-all duration-300 ease-out
                                            ${mobileDropdownOpen === 'investment' ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                                            <div className="ml-4 space-y-1 border-l-2 border-blue-200 pl-4">
                                                {investmentLinks.map((investmentLink, investmentIdx) => (
                                                    <Link
                                                        key={investmentLink.path}
                                                        to={investmentLink.path}
                                                        className={`block py-3 px-4 font-medium transition-all duration-300 relative rounded-lg touch-manipulation
                                                        ${isActive(investmentLink.path)
                                                                ? "text-white bg-gradient-to-r from-blue-500 to-blue-400 shadow-md"
                                                                : "text-gray-600 hover:text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-blue-400 hover:shadow-md"
                                                            }
                                                        hover:scale-[1.02] active:scale-[0.98] transform`}
                                                        aria-current={isActive(investmentLink.path) ? "page" : undefined}
                                                        onClick={closeMobileMenu}
                                                        style={{
                                                            transitionDelay: mobileDropdownOpen === 'investment' ? `${investmentIdx * 30}ms` : "0ms",
                                                            fontFamily: 'Montserrat, Arial, sans-serif'
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <investmentLink.icon className="w-4 h-4" />
                                                            <span className="text-sm">{investmentLink.name}</span>
                                                        </div>
                                                        {isActive(investmentLink.path) && (
                                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 w-1.5 h-1.5 bg-white rounded-full"></div>
                                                        )}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ) : link.name === "Projects" ? (
                                    <div className="space-y-1">
                                        <Link
                                            to={link.path}
                                            className={`block py-4 px-4 font-semibold transition-all duration-300 relative rounded-xl touch-manipulation
                                            ${isActive(link.path)
                                                    ? "text-white bg-gradient-to-r from-blue-600 to-blue-500 shadow-lg"
                                                    : "text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-blue-500 hover:shadow-lg"
                                                }
                                            hover:scale-[1.02] active:scale-[0.98] transform`}
                                            aria-current={isActive(link.path) ? "page" : undefined}
                                            onClick={closeMobileMenu}
                                            style={{
                                                transitionDelay: menuOpen ? `${idx * 50}ms` : "0ms",
                                                fontFamily: 'Montserrat, Arial, sans-serif'
                                            }}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span>{link.name}</span>
                                            </div>
                                            {isActive(link.path) && (
                                                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-white rounded-full"></div>
                                            )}
                                        </Link>

                                        {/* Nigerian Diaspora Dropdown Button */}
                                        <button
                                            onClick={() => toggleMobileDropdown('diaspora')}
                                            className={`w-full flex items-center justify-between py-4 px-4 font-semibold transition-all duration-300 relative rounded-xl touch-manipulation
                                            ${isNigerianDiasporaActive
                                                    ? "text-white bg-gradient-to-r from-blue-600 to-blue-500 shadow-lg"
                                                    : "text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-blue-500 hover:shadow-lg"
                                                }
                                            hover:scale-[1.02] active:scale-[0.98] transform`}
                                            style={{
                                                transitionDelay: menuOpen ? `${(idx + 1) * 50}ms` : '0ms',
                                                fontFamily: 'Montserrat, Arial, sans-serif'
                                            }}
                                        >
                                            <div className="flex items-center gap-3">
                                                <Users className="w-5 h-5" />
                                                <span>Nigerian Diaspora</span>
                                            </div>
                                            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${mobileDropdownOpen === 'diaspora' ? 'rotate-180' : ''}`} />
                                            {isNigerianDiasporaActive && (
                                                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-white rounded-full"></div>
                                            )}
                                        </button>

                                        {/* Nigerian Diaspora Dropdown Items */}
                                        <div className={`overflow-hidden transition-all duration-300 ease-out
                                            ${mobileDropdownOpen === 'diaspora' ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                                            <div className="ml-4 space-y-1 border-l-2 border-blue-200 pl-4">
                                                {nigerianDiasporaLinks.map((diasporaLink, diasporaIdx) => (
                                                    <Link
                                                        key={diasporaLink.path}
                                                        to={diasporaLink.path}
                                                        className={`block py-3 px-4 font-medium transition-all duration-300 relative rounded-lg touch-manipulation
                                                        ${isActive(diasporaLink.path)
                                                                ? "text-white bg-gradient-to-r from-blue-500 to-blue-400 shadow-md"
                                                                : "text-gray-600 hover:text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-blue-400 hover:shadow-md"
                                                            }
                                                        hover:scale-[1.02] active:scale-[0.98] transform`}
                                                        aria-current={isActive(diasporaLink.path) ? "page" : undefined}
                                                        onClick={closeMobileMenu}
                                                        style={{
                                                            transitionDelay: mobileDropdownOpen === 'diaspora' ? `${diasporaIdx * 30}ms` : "0ms",
                                                            fontFamily: 'Montserrat, Arial, sans-serif'
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <diasporaLink.icon className="w-4 h-4" />
                                                            <span className="text-sm">{diasporaLink.name}</span>
                                                        </div>
                                                        {isActive(diasporaLink.path) && (
                                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 w-1.5 h-1.5 bg-white rounded-full"></div>
                                                        )}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    link.name === "Solar" ? (
                                        <a
                                            href={link.path}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`block py-4 px-4 font-semibold transition-all duration-300 relative rounded-xl touch-manipulation
                                        ${isActive(link.path)
                                                ? "text-white bg-gradient-to-r from-blue-600 to-blue-500 shadow-lg"
                                                : "text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-blue-500 hover:shadow-lg"
                                            }
                                        hover:scale-[1.02] active:scale-[0.98] transform`}
                                            onClick={closeMobileMenu}
                                            style={{
                                                transitionDelay: menuOpen ? `${idx * 50}ms` : "0ms",
                                                fontFamily: 'Montserrat, Arial, sans-serif'
                                            }}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span>{link.name}</span>
                                            </div>
                                            {isActive(link.path) && (
                                                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-white rounded-full"></div>
                                            )}
                                        </a>
                                    ) : (
                                        <Link
                                            to={link.path}
                                            className={`block py-4 px-4 font-semibold transition-all duration-300 relative rounded-xl touch-manipulation
                                        ${isActive(link.path)
                                                ? "text-white bg-gradient-to-r from-blue-600 to-blue-500 shadow-lg"
                                                : "text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-blue-500 hover:shadow-lg"
                                            }
                                        hover:scale-[1.02] active:scale-[0.98] transform`}
                                            aria-current={isActive(link.path) ? "page" : undefined}
                                            onClick={closeMobileMenu}
                                            style={{
                                                transitionDelay: menuOpen ? `${idx * 50}ms` : "0ms",
                                                fontFamily: 'Montserrat, Arial, sans-serif'
                                            }}
                                        >
                                            <div className="flex items-center gap-3">
                                                {link.name === "Marketplace" && <Store className="w-5 h-5" />}
                                                <span>{link.name}</span>
                                            </div>
                                            {isActive(link.path) && (
                                                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-white rounded-full"></div>
                                            )}
                                        </Link>
                                    )
                                )}
                            </React.Fragment>
                        ))}

                        {/* Mobile Actions */}
                        <div className="pt-6 border-t border-gray-200/50 space-y-3">
                            {/* App Button */}
                            <Link
                                to="/app"
                                className="relative block w-full py-4 px-4 font-semibold bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-center overflow-hidden group touch-manipulation"
                                onClick={closeMobileMenu}
                                style={{
                                    transitionDelay: menuOpen ? '400ms' : "0ms",
                                    fontFamily: 'Montserrat, Arial, sans-serif'
                                }}
                            >
                                <span className="relative z-10 flex items-center justify-center gap-2">
                                    <Smartphone className="w-5 h-5 transition-transform duration-300 group-hover:rotate-12" />
                                    <span>Get Our App</span>
                                </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                            </Link>

                        {/* Login Section */}
                        {user ? (
                            isRegularUser ? (
                                // User Menu for regular users (mobile)
                                <div className="space-y-2">
                                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                                        <div className="flex items-center gap-3 mb-4">
                                            <UserAvatar 
                                                src={profileImageUrl} 
                                                name={user.name} 
                                                size="lg" 
                                            />
                                            <div>
                                                <h3 className="font-semibold text-gray-900">{user.name || 'User'}</h3>
                                                <p className="text-xs text-gray-600">{user.email}</p>
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            {user?.role === 'DEALER' ? (
                                                <>
                                                    <Link
                                                        to="/dealer/dashboard"
                                                        onClick={closeMobileMenu}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                                    >
                                                        <Home className="w-4 h-4" />
                                                        <span>Dashboard</span>
                                                    </Link>
                                                    <Link
                                                        to={getUserProfilePath()}
                                                        onClick={closeMobileMenu}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                                    >
                                                        <User className="w-4 h-4" />
                                                        <span>Profile</span>
                                                    </Link>
                                                    <button
                                                        onClick={() => {
                                                            handleLogout();
                                                            closeMobileMenu();
                                                        }}
                                                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    >
                                                        <LogOut className="w-4 h-4" />
                                                        <span>Sign Out</span>
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <Link
                                                        to={getUserProfilePath()}
                                                        onClick={closeMobileMenu}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                                    >
                                                        <User className="w-4 h-4" />
                                                        <span>Profile</span>
                                                    </Link>
                                                    <Link
                                                        to={getUserWalletPath()}
                                                        onClick={closeMobileMenu}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                                    >
                                                        <Wallet className="w-4 h-4" />
                                                        <span>Wallet</span>
                                                    </Link>
                                                    <Link
                                                        to={getUserMarketplacePath()}
                                                        onClick={closeMobileMenu}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                                    >
                                                        <Store className="w-4 h-4" />
                                                        <span>Marketplace</span>
                                                    </Link>
                                                    {/* Show Stay/Book for all logged-in users - membership check happens on apartments page */}
                                                    <Link
                                                        to="/apartments"
                                                        onClick={closeMobileMenu}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                                    >
                                                        <Home className="w-4 h-4" />
                                                        <span>Stay</span>
                                                    </Link>
                                                    <Link
                                                        to="/sponsor/children"
                                                        onClick={closeMobileMenu}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                                    >
                                                        <Heart className="w-4 h-4" />
                                                        <span>Sponsor a Child</span>
                                                    </Link>
                                                    <Link
                                                        to="/sponsor/my-children"
                                                        onClick={closeMobileMenu}
                                                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                                    >
                                                        <Heart className="w-4 h-4" />
                                                        <span>My Sponsored Children</span>
                                                    </Link>
                                                    <button
                                                        onClick={() => {
                                                            handleLogout();
                                                            closeMobileMenu();
                                                        }}
                                                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    >
                                                        <LogOut className="w-4 h-4" />
                                                        <span>Sign Out</span>
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // Dashboard Button for admin users (mobile)
                                <Link
                                    to="/admin"
                                    onClick={closeMobileMenu}
                                    className="relative block w-full py-4 px-4 font-semibold bg-green-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-center overflow-hidden group touch-manipulation"
                                    style={{
                                        transitionDelay: menuOpen ? '450ms' : "0ms",
                                        fontFamily: 'Montserrat, Arial, sans-serif'
                                    }}
                                >
                                    <span className="relative z-10 flex items-center justify-center gap-2">
                                        <Shield className="w-5 h-5 transition-transform duration-300 group-hover:rotate-12" />
                                        <span>Dashboard</span>
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-green-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                                </Link>
                            )
                        ) : (
                                // User Login Button - Hide on project detail pages
                                !isProjectDetailPage && (
                                    <a
                                        href="https://app.diasporawise.com"
                                        className="relative block w-full py-4 px-4 font-semibold bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-center overflow-hidden group touch-manipulation"
                                        style={{
                                            transitionDelay: menuOpen ? '450ms' : "0ms",
                                            fontFamily: 'Montserrat, Arial, sans-serif'
                                        }}
                                    >
                                        <span className="relative z-10 flex items-center justify-center gap-2">
                                            <User className="w-5 h-5 transition-transform duration-300 group-hover:rotate-12" />
                                            <span>Login</span>
                                        </span>
                                        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                                    </a>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </nav>

        </>
    );
};

export default Navbar;